<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>People</b-breadcrumb-item>
        </b-breadcrumb>

        <div class="card">
            <div class="bg-dark card-header text-white">{{ showNonTTIHeader ? 'Non-TTI' : 'TTI' }} People</div>
            <div class="card-body">
                <!-- The collapsable filter options -->
                <AppFormCardCollapsable :display-footer="true" @submit.prevent="onFilterSubmit()" class="mb-3">
                    <template v-slot:header>Filter</template>
                    <template v-slot:body>
                        <div class="form-row">
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>Name</label>
                                <input
                                        class="form-control"
                                        placeholder="Name"
                                        v-model="filterOptions.name"
                                />
                            </div>
                            <div class="col-md-2 col-sm-6 form-group">
                                <label>UIN</label>
                                <input
                                        class="form-control"
                                        placeholder="UIN"
                                        v-model="filterOptions.uin"
                                />
                            </div>
                            <div class="col-md-2 col-sm-6 form-group">
                                <label>Organization</label>
                                <input
                                        class="form-control"
                                        placeholder="Organization Code"
                                        v-model="filterOptions.organizationCode"
                                />
                            </div>
                            <div class="col-md-2 col-sm-6 form-group">
                                <label>Employment Type</label>
                                <select class="form-control" v-model="filterOptions.employeeType">
                                    <option :value="null">All</option>
                                    <option :key="type.key" :value="type.value" v-for="type in employeeTypes">{{type.key}}</option>
                                </select>
                            </div>
                            <div class="col-md-2 col-sm-6">
                                <label>AD Account</label>
                                <select class="form-control" v-model="adUserType" @change="updateEmployeeUserType()">
                                    <option :value="null">All</option>
                                    <option :key="type.key" :value="type.value" v-for="type in adUserTypes">{{type.key}}</option>
                                </select>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="col-md-2 col-sm-6 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowActive"
                                        @change="updateEmploymentStatus()"
                                >Only Show Currently Employed
                                </AppCheckBox>
                            </div>
                            <div class="col-md-4 col-sm-6 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowMissingAddress"
                                >Only Show Missing Address
                                </AppCheckBox>
                            </div>
                            <div class="col-md-2 col-sm-6 form-group">
                                <AppCheckBox
                                        v-model="onlyShowNonTTI"
                                        @change="updateEmployeeUserType(); filterOptions.nonTTIProjectEmploymentType = !onlyShowNonTTI ? null : filterOptions.nonTTIProjectEmploymentType;"
                                >Only Show Non-TTI
                                </AppCheckBox>
                            </div>
                            <div v-if="onlyShowNonTTI" class="col-md-2 col-sm-6">
                                <label>Non-TTI Designation</label>
                                <select class="form-control" v-model="filterOptions.nonTTIProjectEmploymentType">
                                    <option :value="null">All</option>
                                    <option :key="designation.item" :value="designation.value" v-for="designation in nonTTIDesignations">{{designation.item}}</option>
                                </select>
                            </div>
                        </div>

                        <div v-if="!filterOptions.onlyShowActive" class="form-row">
                            <div class="col-md-2">
                                <label>Employement Status</label>
                                <select class="form-control" v-model="filterOptions.employmentStatus">
                                    <option :value="null">All</option>
                                    <option :key="status.key" :value="status.value" v-for="status in employmentStatuses">{{status.key}}</option>
                                </select>
                            </div>
                        </div>
                    </template>
                    <template v-slot:footer>
                        <button class="btn btn-primary" type="submit">Apply</button>
                    </template>
                </AppFormCardCollapsable>

                <div class="row">
                    <div class="col d-print-none dropdown mb-3 text-left">
                        <button :disabled="isPerformingAction" aria-expanded="false" aria-haspopup="true"
                                class="btn btn-secondary dropdown-toggle" data-toggle="dropdown" style="width: 175px"
                                type="button">
                            <span>Pay Indicator Key</span>
                        </button>
                        <div class="dropdown-menu">
                            <h6 class="dropdown-header">A: Salaried Faculty or Staff with FTE &gt; 50</h6>
                            <h6 class="dropdown-header">B: Salaried Staff with FTE &lt;	51</h6>
                            <h6 class="dropdown-header">C: Graduate Assistant</h6>
                            <h6 class="dropdown-header">D: Hourly Staff</h6>
                            <h6 class="dropdown-header">E: Hourly Faculty or Temporary/Casual Staff</h6>
                            <h6 class="dropdown-header">F: Hourly Staff with FTE &lt;	51 or Student Worker</h6>
                        </div>
                    </div>
                    
                    <div class="col d-print-none dropdown mb-3 text-right">
                        <button :disabled="isPerformingAction" aria-expanded="false" aria-haspopup="true"
                                class="btn btn-secondary dropdown-toggle" data-toggle="dropdown" style="width: 100px"
                                type="button">
                            <span>Actions</span>
                        </button>
                        <div class="dropdown-menu">
                            <a @click.prevent="downloadCsvExport()" class="btn btn-outline-light dropdown-item text-left"
                              href="javascript:void(0)" title="Export account list to a CSV">
                                <i class="fa fa-file-excel"></i>Download .csv
                            </a>
                        </div>
                    </div>
                </div>

                <BTable
                        :busy="isLoading"
                        :fields="tableFields"
                        :items="people"
                        :responsive="true"
                        primary-key="id"
                        show-empty
                        striped
                        table-class="table-align-middle">

                    <template v-slot:cell(id)="row">
                        <router-link :to="{name:'PersonDetails',params:{id:row.item.id, personName:row.item.name, personUIN:row.item.uin}}"
                                     class="btn btn-sm btn-warning d-print-none"
                                     data-toggle="tooltip"
                                     title="Edit person details">
                            <i class="fa fa-pencil-alt"></i>Edit
                        </router-link>
                    </template>

                    <template v-slot:cell(emailAddress)="row">
                        <AppDisplayTemplateEmail :email-address="row.item.emailAddress"/>
                    </template>

                    <template v-slot:cell(inAD)="row">
                        <AppDisplayTemplateBoolean :value="row.item.inAD"></AppDisplayTemplateBoolean>
                        {{ row.item.expiredInAD ? "Expired" : ""}}
                    </template>

                    <!-- Footer Pagination -->
                    <template v-slot:custom-foot>
                        <b-tr>
                            <b-td :colspan="tableFields.length">
                                <AppTablePagination
                                        :page.sync="filterOptions.page"
                                        :pageSize.sync="filterOptions.pageSize"
                                        :totalCount.sync="totalPeople"
                                        v-on:update:page="loadData()"
                                        v-on:update:pageSize="loadData()"
                                ></AppTablePagination>
                            </b-td>
                        </b-tr>
                    </template>

                </BTable>

            </div>
        </div>

    </div>
</template>

<script>
    import PersonService from "@/services/PersonService";
    import AppFormCardCollapsable from "@/components/shared/AppFormCardCollapsable";
    import AppCheckBox from "@/components/shared/AppCheckBox";
    import AppTablePagination from "@/components/shared/AppTablePagination";
    import AppDisplayTemplateEmail from "@/components/shared/AppDisplayTemplateEmail";
    import AppDisplayTemplateBoolean from "@/components/shared/AppDisplayTemplateBoolean";
    import {ToastType} from "@/services/models/shared/ToastType";
    import {EmployeeUserType} from "@/services/models/shared/EmployeeUserType";
    import {ProjectEmployeeType} from "@/services/models/shared/ProjectEmployeeType";

    export default {
        components: {
            AppFormCardCollapsable,
            AppCheckBox,
            AppTablePagination,
            AppDisplayTemplateEmail,
            AppDisplayTemplateBoolean
        },
        data() {
            return {
                isPerformingAction: false,
                people: [],
                totalPeople: 0,
                filterOptions: {
                    page: 1,
                    pageSize: 10,
                    name: null,
                    uin: null,
                    employeeUserType: EmployeeUserType.TTIEmployee,
                    code: null,
                    onlyShowActive: true,
                    onlyShowMissingAddress: false,
                    onlyShowTTI: false,
                    employeeType: null,
                    employmentStatus: null,
                    nonTTIProjectEmploymentType: null,
                    onlyShowNotInAD: false
                },
                isLoading: false,
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:100px'
                    },
                    {
                        key: 'name',
                        label: 'Name'
                    },
                    {
                        key: 'uin',
                        label: 'UIN'
                    },
                    {
                        key: 'organizationCode',
                        label: 'TTI Org Code',
                        thStyle: 'width:90px'
                    },
                    {
                        key: 'supervisorName',
                        label: 'Supervisor'
                    },
                    {
                        key: 'employmentStatus',
                        label: 'Status'
                    },
                    {
                        key: 'employeeType',
                        label: 'Employment Type'
                    },
                    {
                        key: 'inAD',
                        label: 'Active Directory'
                    },
                    {
                        key: 'payIndicator',
                        label: 'Pay Indicator'
                    }
                ],
                onlyShowNonTTI: false,
                onlyShowInAD: false,
                onlyShowExpired: false,
                showNonTTIHeader: false,
                adUserType: null,
                adUserTypes: [
                    {
                        key: "Not in AD",
                        value: 0
                    },
                    {
                        key: "Active",
                        value: 1
                    },
                    {
                        key: "Expired",
                        value: 8
                    }
                ],
                employeeTypes: [
                    {
                        key: "-",
                        value: "-"
                    },
                    {
                        key: "Faculty",
                        value: "Faculty"
                    },
                    {
                        key: "Graduate Assistant",
                        value: "Graduate Assistant"
                    },
                    {
                        key: "Non-Employee Benefits Only",
                        value: "Non-Employee Benefits Only"
                    },
                    {
                        key: "Staff",
                        value: "Staff"
                    },
                    {
                        key: "Student Worker",
                        value: "Student Worker"
                    },
                    {
                        key: "Temporary/Casual Staff",
                        value: "Temporary/Casual Staff"
                    }
                ],
                employmentStatuses: [
                    {
                        key: "Active",
                        value: "A"
                    },
                    {
                        key: "Deceased",
                        value: "D"
                    },
                    {
                        key: "Leave Without Pay",
                        value: "L"
                    },
                    {
                        key: "Affiliate Non-Employee",
                        value: "N"
                    },
                    {
                        key: "Retired",
                        value: "R"
                    },
                    {
                        key: "Terminated",
                        value: "T"
                    },
                    {
                        key: "Modified Service",
                        value: "W"
                    },
                    {
                        key: "Graduate Fellow",
                        value: "F"
                    }
                ],
                nonTTIDesignations: []
            }
        },
        methods: {
            /**
             * @description Loads the data from the API with the filter options bound to the component
             */
            loadData() {
                const vm = this;
                vm.isLoading = true;

                this.tableFields = !this.onlyShowNonTTI ? [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:100px'
                    },
                    {
                        key: 'name',
                        label: 'Name'
                    },
                    {
                        key: 'uin',
                        label: 'UIN'
                    },
                    {
                        key: 'organizationCode',
                        label: 'TTI Org Code',
                        thStyle: 'width:90px'
                    },
                    {
                        key: 'supervisorName',
                        label: 'Supervisor'
                    },
                    {
                        key: 'employmentStatus',
                        label: 'Status'
                    },
                    {
                        key: 'employeeType',
                        label: 'Employment Type'
                    },
                    {
                        key: 'inAD',
                        label: 'Active Directory'
                    },
                    {
                        key: 'payIndicator',
                        label: 'Pay Indicator'
                    }
                ] : [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:100px'
                    },
                    {
                        key: 'name',
                        label: 'Name'
                    },
                    {
                        key: 'uin',
                        label: 'UIN'
                    },
                    {
                        key: 'supervisorName',
                        label: 'Supervisor'
                    },
                    {
                        key: 'externalOrganization',
                        label: 'Supervisory Organization'
                    },
                    {
                        key: 'organizationCode',
                        label: 'TTI Org Code',
                        thStyle: 'width:90px'
                    },
                    {
                        key: 'employmentStatus',
                        label: 'Status'
                    },
                    {
                        key: 'employeeType',
                        label: 'Employment Type'
                    },
                    {
                        key: 'inAD',
                        label: 'Active Directory'
                    },
                    {
                        key: 'nonTTIProjectEmployeeType',
                        label: 'Non-TTI Designation'
                    },
                    {
                        key: 'payIndicator',
                        label: 'Pay Indicator'
                    }
                ]

                PersonService.getPeopleList(vm.filterOptions)
                    .then(response => {
                        vm.people = response.data;
                        vm.totalPeople = response.totalCount;
                    })
                    .catch(() => {
                        vm.redirectToError()
                    })
                    .finally(() => {
                        vm.showNonTTIHeader = this.onlyShowNonTTI;
                        vm.isLoading = false;
                    })
            },

            /**
             * @description Resets the page to the first page and reloads the data
             */
            onFilterSubmit() {
                this.filterOptions.page = 1;
                this.loadData();
            },

            /**
             * @description Downloads the support accounts CSV export to the user's browse
             */
            downloadCsvExport() {
                this.isPerformingAction = true;
                const vm = this;

                PersonService.getExcelExport(this.filterOptions)
                    .catch(error => {
                        vm.alert(ToastType.Danger, error.response.statusText)
                    })
                    .finally(() => {
                        this.isPerformingAction = false
                    });
            },

            updateEmployeeUserType() {
              this.filterOptions.employeeUserType = null;
              this.filterOptions.onlyShowNotInAD = false;

              if (!this.onlyShowNonTTI) {
                  this.filterOptions.employeeUserType = EmployeeUserType.TTIEmployee;
              } else {
                  this.filterOptions.employeeUserType = EmployeeUserType.NonTTIEmployee;
              }


              if (this.adUserType !== null) {
                  if (this.adUserType === EmployeeUserType.ADUser || this.adUserType === EmployeeUserType.ADUserExpired) {
                      this.filterOptions.employeeUserType += EmployeeUserType.ADUser;
                      if (this.adUserType === EmployeeUserType.ADUserExpired) {
                          this.filterOptions.employeeUserType += EmployeeUserType.ADUserExpired;
                      }
                  } else {
                      this.filterOptions.onlyShowNotInAD = true;
                  }
              }
            },

            updateEmploymentStatus() {
                if (this.filterOptions.onlyShowActive) {
                    this.filterOptions.employmentStatus = null;
                }
            }
        },
        created() {
            this.loadData();
            this.nonTTIDesignations = (Object.keys(ProjectEmployeeType).filter(x => isNaN(parseInt(x)))).map((key) => {
                const enumVal = ProjectEmployeeType[key]
                return {
                    value: enumVal,
                    item: key
                  }
              })
        }
    }
</script>
